import React from "react";
import {
  Popup,
  List,
  Button,
  Text,
  Flex,
  Loader,
} from "@fluentui/react-northstar";
import {
  AcceptIcon,
  BanIcon,
  CheckmarkCircleIcon,
  ShareGenericIcon,
  BellIcon,
  TrashCanIcon,
  SyncIcon,
} from "@fluentui/react-icons-northstar";
import { formatDistanceToNow } from "date-fns";
import { useNotification } from "../../context/NotificationContext";
import { useHistory } from "react-router-dom";
import { Empty, Spin } from "antd";

const NotificationPopup = ({ trigger }) => {
  const history = useHistory();
  const {
    notifications,
    isLoadingNotifications,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
    getNotifications,
    deleteNotification,
  } = useNotification();

  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await getNotifications();
    setIsRefreshing(false);
  };

  const getNotificationIcon = (type) => {
    const iconStyle = {
      marginTop: "8px",
      width: "16px",
      height: "16px",
    };

    const icon = (() => {
      switch (type) {
        case "request-approved":
          return <AcceptIcon outline size="medium" color="green" />;
        case "request-rejected":
          return <BanIcon outline size="medium" color="red" />;
        case "shared-spinWheel-added":
        case "shared-collection-added":
          return <ShareGenericIcon outline size="medium" color="brand" />;
        case "request-sent":
          return <CheckmarkCircleIcon outline size="medium" color="orange" />;
        default:
          return <BellIcon outline size="medium" color="grey" />;
      }
    })();

    return <div style={iconStyle}>{icon}</div>;
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }

    switch (notification.type) {
      case "shared-spinWheel-added":
        // history.push(`/spin-wheel/${notification.metadata.spinWheelName}`);
        break;
      case "shared-collection-added":
        // history.push(`/collections/${notification.metadata.collectionId}`);
        break;
      case "request-approved":
      case "request-rejected":
      case "request-sent":
        // history.push("/trusted-network");
        break;
      default:
        break;
    }
  };

  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation(); // Prevent notification click event
    try {
      await deleteNotification(notificationId);
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const renderNotification = (notification) => ({
    key: notification.id,
    media: getNotificationIcon(notification.type),
    content: (
      <Flex space="between" vAlign="center" style={{ width: "100%" }}>
        <Flex column gap="gap.smaller" style={{ flex: 1, padding: "8px 12px" }}>
          <Text weight="semibold" content={notification.title} />
          <Text
            size="small"
            content={notification.message}
            style={{ color: "#666" }}
          />
          <Text
            size="smaller"
            content={formatDistanceToNow(new Date(notification.createdAt), {
              addSuffix: true,
            })}
            style={{ color: "#999" }}
          />
        </Flex>
        <Button
          text
          iconOnly
          size="small"
          icon={<TrashCanIcon outline size="small" />}
          onClick={(e) => handleDeleteNotification(e, notification.id)}
          style={{
            marginTop: "8px",
            alignSelf: "self-start",
          }}
          title="Delete notification"
        />
      </Flex>
    ),
    onClick: () => handleNotificationClick(notification),
    styles: {
      display: "flex",
      alignItems: "flex-start",
      backgroundColor: notification.isRead ? "transparent" : "#f0f0ff",
      cursor: "pointer",
      margin: "4px 0",
      borderRadius: "8px",
      transition: "all 0.2s ease",
      ":hover": {
        backgroundColor: "#e6e6ff",
        transform: "translateX(4px)",
        "& button": {
          opacity: 1,
        },
      },
    },
  });

  const content = (
    <Flex column style={{ width: "380px", maxHeight: "600px" }}>
      <Flex space="between" vAlign="center" style={{ padding: "12px 16px" }}>
        <Flex gap="gap.small" vAlign="center">
          <Text weight="bold" size="large" content="Notifications" />
          <Button
            text
            size="small"
            icon={
              <SyncIcon
                style={{
                  transform: isRefreshing ? "rotate(180deg)" : "none",
                  transition: "transform 0.5s",
                }}
              />
            }
            iconOnly
            title="Refresh"
            onClick={handleRefresh}
            disabled={isLoadingNotifications || isRefreshing}
          />
        </Flex>
        {notifications.length > 0 && (
          <Flex gap="gap.smaller">
            {notifications.some((n) => !n.isRead) && (
              <Button
                text
                size="small"
                iconOnly
                icon={<CheckmarkCircleIcon style={{ marginRight: "4px" }} />}
                content="Mark all read"
                onClick={markAllAsRead}
              />
            )}
            <Button
              text
              size="small"
              icon={<TrashCanIcon style={{ marginRight: "4px" }} />}
              content="Clear"
              onClick={clearAllNotifications}
              iconOnly
            />
          </Flex>
        )}
      </Flex>

      <div
        style={{
          overflowY: "auto",
          maxHeight: "500px",
          padding: "0 8px",
        }}
      >
        {isLoadingNotifications ? (
          <Flex hAlign="center" vAlign="center" style={{ height: "154px" }}>
            <Loader />
          </Flex>
        ) : notifications.length === 0 ? (
          <Flex hAlign="center" padding="padding.medium">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No new notifications"
            />
          </Flex>
        ) : (
          <List items={notifications.map(renderNotification)} selectable />
        )}
      </div>
    </Flex>
  );

  return (
    <Popup
      trigger={trigger}
      content={content}
      pointing
      position="below"
      align="end"
      trapFocus
      style={{
        padding: 0,
        border: "1px solid #e8e8e8",
        borderRadius: "12px",
        boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
      }}
    />
  );
};

export default NotificationPopup;
